import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Loader2, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'checking', txn: null, attempts: 0 });
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!sessionId) return setState({ status: 'error', txn: null });

    const poll = async (attempt) => {
      if (cancelled.current) return;
      if (attempt >= 8) return setState({ status: 'timeout', txn: null });
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled.current) return;
        if (data.payment_status === 'paid') {
          setState({ status: 'success', txn: data });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', txn: data });
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        if (!cancelled.current) setState({ status: 'error', txn: null });
      }
    };
    poll(0);
    return () => { cancelled.current = true; };
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 max-w-xl mx-auto px-6">
      <div className="glass-strong rounded-3xl border border-white/10 p-12 text-center">
        {state.status === 'checking' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-fuchsia-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl text-white">Confirming your payment…</h1>
            <p className="text-slate-400 mt-2 text-sm">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === 'success' && (
          <>
            <div className="inline-flex h-16 w-16 rounded-full bg-emerald-500/20 items-center justify-center mb-4">
              <CheckCircle2 className="h-9 w-9 text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl text-white font-bold mb-2">Payment successful</h1>
            <p className="text-slate-300 mb-6">
              {state.txn?.kind === 'premium' && <>Your <span className="rainbow-text font-semibold">{state.txn.plan_id}</span> plan is live.</>}
              {state.txn?.kind === 'ticket' && <>{state.txn.quantity} ticket{state.txn.quantity > 1 ? 's' : ''} booked. Check "My Tickets" in your dashboard.</>}
            </p>
            <div className="text-slate-500 text-xs mb-6">₹{state.txn?.amount?.toLocaleString('en-IN')} · {state.txn?.currency?.toUpperCase()}</div>
            <Link to="/dashboard" className="btn-rainbow rounded-full px-7 py-3 text-white inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Go to dashboard
            </Link>
          </>
        )}
        {(state.status === 'expired' || state.status === 'timeout' || state.status === 'error') && (
          <>
            <div className="inline-flex h-16 w-16 rounded-full bg-rose-500/20 items-center justify-center mb-4">
              <XCircle className="h-9 w-9 text-rose-400" />
            </div>
            <h1 className="font-display text-3xl text-white font-bold mb-2">
              {state.status === 'expired' ? 'Session expired' : state.status === 'timeout' ? 'Still processing' : 'Something went wrong'}
            </h1>
            <p className="text-slate-400 mb-6 text-sm">
              {state.status === 'timeout' ? "We're still waiting on confirmation. Refresh this page in a minute." : 'Please try again.'}
            </p>
            <Link to="/dashboard" className="text-fuchsia-300 hover:text-fuchsia-200">Back to dashboard →</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
